'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'

import { KiteLogo } from '@/app/components/KiteLogo'
import { Navigation } from '@/app/components/Navigation'
import { Social } from '@/app/components/Social'
import { TRACKING_LOCATIONS } from '@/app/utils/analytics'
import { cn } from '@/app/utils/style'
import { getLocaleFromPathname } from '@/lib/i18n'
import { NavQueryResult, StructuredDataQueryResult } from '@/sanity.types'

interface FooterProps {
  className?: string
  nav: NavQueryResult[number] | null
  nav2: NavQueryResult[number] | null
  navLegal: NavQueryResult[number] | null
  socialProfiles?: NonNullable<
    NonNullable<StructuredDataQueryResult>['organizationShared']
  >['socialProfiles']
}

export const Footer = function Footer({
  className,
  nav: initialNav,
  nav2: initialNav2,
  navLegal: initialNavLegal,
  socialProfiles,
}: FooterProps) {
  const [nav, setNav] = useState(initialNav)
  const [nav2, setNav2] = useState(initialNav2)
  const [navLegal, setNavLegal] = useState(initialNavLegal)
  const pathname = usePathname()
  const locale = getLocaleFromPathname(pathname)

  useEffect(() => {
    setNav(initialNav)
    setNav2(initialNav2)
    setNavLegal(initialNavLegal)
  }, [initialNav, initialNav2, initialNavLegal])

  useEffect(() => {
    const locale = getLocaleFromPathname(pathname)

    fetch(`/api/navigations?locale=${locale}`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json()
      })
      .then((data) => {
        setNav(data.footer)
        setNav2(data.footer_2)
        setNavLegal(data.footer_legal)
      })
      .catch((err) => console.error('Failed to fetch navigations:', err))
  }, [pathname])

  return (
    <footer className={cn('bg-neutral-50', className)}>
      <div className="flex flex-col pt-md pb-lg px-lg gap-5xl md:px-5xl md:py-5xl md:flex-row md:gap-2xl md:items-start lg:px-7xl">
        <Link className="w-fit md:flex-shrink-0 md:w-[72px]" href="/" aria-label="Kite Mobile">
          <KiteLogo className="w-[59px] h-[18px] md:w-[72px] md:h-[22px]" />
        </Link>

        <div className="flex flex-col gap-5xl md:flex-row md:gap-2xl md:flex-1 md:justify-end lg:gap-9xl">
          <Navigation
            classNames={{
              root: 'flex flex-col gap-xl md:min-w-[160px]',
              label: 'text-primary-700',
              list: 'flex flex-col gap-md',
              item: 'font-medium size-body-sm text-neutral-900 hover:text-primary-600 active:text-primary-200 transition-colors duration-250',
            }}
            label={nav?.title}
            nav={nav}
            tracking={{ location: TRACKING_LOCATIONS.FOOTER }}
          />

          <Navigation
            classNames={{
              root: 'flex flex-col gap-xl md:min-w-[160px]',
              label: 'text-primary-700',
              list: 'flex flex-col gap-md',
              item: 'font-medium size-body-sm text-neutral-900 hover:text-primary-600 active:text-primary-200 transition-colors duration-250',
            }}
            label={nav2?.title}
            nav={nav2}
            tracking={{ location: TRACKING_LOCATIONS.FOOTER }}
          />

          <div className="flex flex-col gap-5xl md:justify-between">
            <div className="flex flex-col gap-xl">
              <p className="size-overline-sm leading-overline-sm tracking-overline-sm font-black uppercase text-primary-700">
                {locale === 'es' ? 'Síguenos' : 'Follow us'}
              </p>
              <Social profiles={socialProfiles} />
            </div>

            <Navigation
              classNames={{
                list: 'flex flex-row gap-sm',
                item: 'font-medium size-body-sm text-neutral-900 hover:text-primary-600 active:text-primary-200 transition-colors duration-250',
                separator: 'font-medium size-body-sm before:content-["|"] before:text-neutral-900',
              }}
              nav={navLegal}
              tracking={{ location: TRACKING_LOCATIONS.FOOTER }}
              includeSeparator
            />
          </div>
        </div>
      </div>
    </footer>
  )
}
